/// <amd-module name="typeahead/core/EssDataProvider" />
/// <amd-dependency path="jquery" name="$" />

/// <reference path="../../typings/typeahead/ExpediaSuggestServiceRequestGenerator.d.ts" />

"use strict";
import {IDataProvider, IDataProviderResponse} from "typeahead/core/IDataProvider";

export interface IEssResponse extends IDataProviderResponse {
    query: string;
    requestId: string;
    responseCode: string;
    isReferralToGoogleMaps: boolean;
}

export enum GoogleRequestType {GOOGLE_AUTOCOMPLETE, ADDRESS_AUTOCOMPLETE}

export class EssDataProvider implements IDataProvider {

    private static isReferralToGoogleMaps(responseCode: string): boolean {
        return ([GoogleRequestType[GoogleRequestType.ADDRESS_AUTOCOMPLETE],
            GoogleRequestType[GoogleRequestType.GOOGLE_AUTOCOMPLETE]].indexOf(responseCode) > -1);
    }

    /**
     * Function that given a search query string, a flag for ignoring results (used as a seed call)
     * and a set of options, returns a Promise that when it resolves contains an object with two properties.
     *  1) a results list containing all the items returned from the call
     *  2) the original search query used to make the HTTP request(s)
     * @param srchText - search query string
     * @param ignoreResults - fire the request and ignore the response
     * @param options - object hash containing the list of options.
     * @param $ - reference to jQuery instance to use
     * NOTE: the required properties for the options hash are:
     *  {
     *    additionalDataSourceUrl: "",
     *    version: "v2",
     *    overrideServiceUrl: "",
     *    serviceUrl: "//suggest.expedia.com",
     *    mask: defaultMask,
     *    locale: "en_US",
     *    abtest: "",
     *    lob: LOB_ATTRIBUTE.FLIGHTS,
     *    dest: false,
     *    maxitems: 20,
     *    clientId: "",
     *    siteId: "",
     *    guid: "",
     *    features: ""
     *  }
     * @returns an instance of a Promise
     */
    public fetchData(options: any): JQueryPromise<IEssResponse> {
        const requestGenerator: IRequestGenerator = options.requestGenerator as IRequestGenerator;
        const context: IContext = options.context as IContext;
        const essServicePromise: JQueryPromise<any> = requestGenerator.makeRequest(options.srchText, options, context);

        if (options.overrideDataRequest && typeof options.overrideDataRequest === "function") {
            return options.overrideDataRequest(options.srchText)
                .then(this.singleSourceResponseHandler);
        }
        if (options.ignoreResults) {
            return essServicePromise.then($.noop);
        }
        return essServicePromise.then(this.singleSourceResponseHandler);
    }

    private singleSourceResponseHandler(jsonResponseData): IEssResponse {
        let results = jsonResponseData.sr || jsonResponseData.r || [];
        const query = jsonResponseData.q;

        if (jsonResponseData && jsonResponseData.homeAirport) {
            jsonResponseData.homeAirport.category = "HOMEAIRPORT";
            results = results.concat(jsonResponseData.homeAirport);
        }

        return {
            activeABTests: jsonResponseData.activeABTests,
            isReferralToGoogleMaps: EssDataProvider.isReferralToGoogleMaps(jsonResponseData.rc),
            metadata: jsonResponseData.metadata,
            overriddenABTests: jsonResponseData.overriddenABTests,
            query,
            requestId: jsonResponseData.rid,
            responseCode: jsonResponseData.rc,
            results: (results || []),
        };
    }
}

/**
 * Function to allow the caller to provide the version of jQuery they want to use
 * and not rely on the page load time AMD module loader to provide one. This facilitates
 * unit testing so you can mock/stub/fake the jQuery functionality used in here.
 * @param jQuery
 */
export function overrideJQuery(jQuery: JQueryStatic): void {
    $ = jQuery;
}
